import { useToast } from '@contexts/Toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { Axios } from '@helpers/ApiService';
import { errorMessage, WeightType } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  AddonsList,
  ProductDetails,
  SelectedProduct,
  ModifiedSelectedProduct,
} from '@helpers/Models';
import { useEffect, useState } from 'react';

export const ProductAddonListSheet: React.FC<{
  productDetails: ProductDetails;
  onClose: () => void;
  addonData: ModifiedSelectedProduct[];
  isDeletionMode: boolean;
  onAddCustomization: (customization: SelectedProduct) => void;
  showAddonSelectionSheet: (value: boolean) => void;
  onDeleteCustomization: (index: number) => void;
}> = ({
  productDetails,
  onClose,
  addonData,
  isDeletionMode,
  onAddCustomization,
  onDeleteCustomization,
  showAddonSelectionSheet,
}) => {
  const [addons, setAddons] = useState<AddonsList[]>([]);
  const { showToastMessage } = useToast();
  const [isDeletion, setIsDeletion] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [customizations, setCustomizations] = useState(addonData);

  useEffect(() => {
    fetchAddons();
    if (isDeletionMode) setIsDeletion(true);
    else setIsDeletion(false);
    console.log('productDetails', productDetails);
  }, []);

  /**
   * Fetches add-on items from the server based on a search key.
   *
   * Sends a POST request to the `FETCH_ADDONS` API endpoint with the provided `searchKey`.
   * On success:
   * - Updates the `addons` state with the fetched add-on list.
   * On failure:
   * - Displays an error toast message.
   * - Rethrows the error for potential upstream error handling.
   *
   * @param {string} searchKey - The string used to filter or search add-ons on the server.
   * @returns {Promise<void>} A promise that resolves when the fetch and state update are complete.
   *
   * Dependencies:
   * - Axios for HTTP requests.
   * - ApiEndpoints.FETCH_ADDONS
   * - showToastMessage for user feedback.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles deletion of a specific add-on customization from the UI and backend state.
   *
   * Removes the customization at the specified `index` from the `customizations` state,
   * and triggers a callback to handle any additional cleanup or persistence logic.
   *
   * @param {number} customizationIndex - The index used to identify the customization in external state (e.g., backend or parent component).
   * @param {number} index - The index of the customization to remove from the local `customizations` state array.
   * @returns {Promise<void>} A promise that resolves after state is updated and the external delete handler is called.
   *
   * Side Effects:
   * - Updates local `customizations` state via `setCustomizations`.
   * - Calls the `onDeleteCustomization` callback with the relevant index.
   */
  const handleDeleteAddon = async (
    customizationIndex: number,
    index: number,
  ): Promise<void> => {
    setCustomizations((prev) => {
      return prev.filter((_, idx) => idx !== index);
    });
    onDeleteCustomization(customizationIndex);

    //when there is no customization left, close the sheet
    if (customizations.length === 1) {
      onClose();
    }
  };

  return (
    <div className="fixed z-10 inset-0 bg-black bg-opacity-40 flex justify-center items-end w-full h-full">
      <div className="bg-white rounded-t-2xl w-full p-6 shadow-lg transition-transform transform translate-y-0 max-h-[70vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-semibold text-lg">
            {productDetails.name} Customization
          </h1>
          <div className="flex items-end gap-2">
            <button
              onClick={onClose}
              type="button"
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="border-t border-gray-400 w-full" />

        <div className="mt-4 max-h-[60vh] overflow-y-auto pr-2">
          <div className="flex justify-between items-center mb-4">
            <h1 className="font-semibold text-lg">Your Customization</h1>
          </div>
          <div className="mb-4">
            <div className="items-center">
              {customizations.map((customization, index) => {
                const addonNames = customization.addOnIds
                  .map((addonId) => {
                    const addon = addons.find((a) => a.id === addonId);
                    return addon?.name;
                  })
                  .filter(Boolean)
                  .join(', ');
                return (
                  <div
                    key={index}
                    className="flex justify-between gap-5 items-stretch mb-4"
                  >
                    <div className="w-1/2">
                      <div className="h-full border p-4 rounded-lg shadow-md flex flex-col justify-between bg-tertiary border-primary">
                        <div className="flex justify-between font-semibold text-lg text-gray-700">
                          <div>
                            {productDetails.name}
                            {customization.fatPercent > 0 &&
                              `, ${customization.fatPercent}% Fat`}
                          </div>
                          <div>
                            {customization.quantity}{' '}
                            {productDetails.productType == 1
                              ? `${WeightType.LBS}`
                              : 'Qty'}
                          </div>
                        </div>
                        <div className="mt-10">
                          <span className="text-sm text-gray-600 mb-3 font-semibold">
                            {addonNames}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="w-1/2 flex items-center justify-center">
                      <div className="w-full">
                        {isDeletion ? (
                          <button
                            onClick={() =>
                              handleDeleteAddon(customization.index, index)
                            }
                            type="button"
                            className="w-full px-6 py-2 border rounded-sm text-sm font-medium text-red-600 bg-white border-red-600"
                          >
                            Delete
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setConfirm(true);
                              const repeatedCustomization = {
                                ...customization,
                              };
                              setCustomizations((prev) => [
                                ...prev,
                                repeatedCustomization,
                              ]);
                              onAddCustomization(repeatedCustomization);
                            }}
                            type="button"
                            className="w-full px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary flex items-center justify-center space-x-2 gap-2"
                          >
                            <FontAwesomeIcon icon={faRepeat} />
                            Duplicate {/* Previously Repeat Customization */}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {!isDeletion && (
            <div className="sticky bottom-0 bg-white pt-4 pb-2 mt-4 border-t border-gray-300">
              <button
                onClick={() => {
                  showAddonSelectionSheet(true);
                  onClose();
                }}
                type="button"
                className="w-full px-6 py-1 border rounded-sm text-sm font-medium text-primary bg-white hover:bg-primaryF"
              >
                Add New Customization
              </button>
              {confirm && (
                <button
                  onClick={onClose}
                  type="button"
                  className="w-full px-6 py-1 mt-4 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary"
                >
                  Confirm
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
